import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { showError, showSuccess, showLoading, closeLoading, confirmarEliminacion } from '../utils/alerts';
import { API_BASE_URL } from '../config';
import useMesesCerrados from '../hooks/useMesesCerrados';
import { eliminarPeriodoCarga } from '../services/cargasPeriodoService';
import '../styles/pages/CargaCompras.css';
const meses = [
  'Enero',
  'Febrero',
  'Marzo',
  'Abril',
  'Mayo',
  'Junio',
  'Julio',
  'Agosto',
  'Septiembre',
  'Octubre',
  'Noviembre',
  'Diciembre'
];


const CargaVentas = ({ token, id_company }) => {

    const [year, setYear] = useState('');
    const [files, setFiles] = useState(Array(12).fill(null));
    const [status, setStatus] = useState(Array(12).fill({ cargado: false, procesado: false }));
    const [loadingSii, setLoadingSii] = useState(Array(12).fill(false));
    const mesesCerrados = useMesesCerrados(id_company, year, token);
    const mesCerrado = (index) => !!mesesCerrados[index + 1];
    // Un mes cargado no se vuelve a cargar ni consultar: primero hay que eliminarlo
    const bloqueado = (index) => mesCerrado(index) || status[index].cargado;
    const motivoBloqueo = (index) => mesCerrado(index)
      ? 'Mes cerrado'
      : status[index].cargado ? 'Mes ya cargado: elimínelo para volver a cargarlo' : undefined;

    useEffect(() => {
      if (!year || !token) return;
      const fetchStatus = async () => {
        try {
          const response = await axios.get(`${API_BASE_URL}/api/estado-ventas`, {
            params: { id_company, anual: year },
            headers: { Authorization: `Bearer ${token}` }
          });
          const newStatus = Array(12).fill({ cargado: false, procesado: false });
          response.data.forEach(row => {
            const idx = row.mes - 1;
            newStatus[idx] = { cargado: row.cargado, procesado: row.procesado };
          });
          setStatus(newStatus);
        } catch (error) {
          console.error('Error obteniendo estado de ventas', error);
        }
      };
      fetchStatus();
    }, [year, id_company, token]);

    const handleFileChange = (index, file) => {
    const newFiles = [...files];
    newFiles[index] = file;
    setFiles(newFiles);
  };

    const handleUpload = async (index) => {
    const file = files[index];
    if (!file || !year) {
        showError('Por favor selecciona un archivo y un año');
      return;
    }

    const formData = new FormData();
    formData.append('file', file);
    formData.append('id_company', id_company);
    formData.append('mes', index + 1);
    formData.append('anual', year);

    try {
      // Cargar también contabiliza el período: si algo falla no queda nada guardado
      showLoading('Cargando y contabilizando ventas', `${meses[index]} ${year}`);
      const response = await axios.post(`${API_BASE_URL}/api/cargar/ventas`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      closeLoading();
      showSuccess(response.data.message);
      const newStatus = [...status];
      newStatus[index] = { cargado: true, procesado: true };
      setStatus(newStatus);
      const newFiles = [...files];
      newFiles[index] = null;
      setFiles(newFiles);
    } catch (error) {
      closeLoading();
      console.error('Error al cargar el archivo', error);
      showError('No se cargó el archivo', error.response?.data?.error || 'Error al cargar el archivo');
    }
  };

  // Deshace la carga del mes: borra sus facturas de venta, sus asientos y su estado, para
  // volver a cargar o consultar desde cero. La fila de un mes cerrado está deshabilitada.
  const handleEliminar = async (index) => {
    const confirmado = await confirmarEliminacion(
      `¿Eliminar las ventas de ${meses[index]} ${year}?`,
      'Se eliminarán:<br><br>• Las facturas de venta del mes<br>• Sus asientos contables<br><br>Después podrás volver a cargar o consultar el mes.'
    );
    if (!confirmado) return;

    try {
      const r = await eliminarPeriodoCarga('ventas', id_company, year, index + 1, token);
      showSuccess(r.message);
      const newStatus = [...status];
      newStatus[index] = { cargado: false, procesado: false };
      setStatus(newStatus);
    } catch (error) {
      console.error('Error al eliminar el período', error);
      showError(error.response?.data?.error || 'Error al eliminar las ventas del mes');
    }
  };

  const consultarSii = async (index) => {
    if (!year) {
      showError('Por favor selecciona un año');
      return;
    }

    const newLoadingSii = [...loadingSii];
    newLoadingSii[index] = true;
    setLoadingSii(newLoadingSii);

    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/consultar-sii-ventas`,
        { id_company, year: parseInt(year), month: index + 1 },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          timeout: 120000
        }
      );

      if (response.data.success) {
        const stats = response.data.data?.data?.stats || response.data.data?.stats;
        if (stats) {
          showSuccess(`${meses[index]}: ${stats.totalParsed || 0} registros obtenidos y procesados del SII`);
        } else {
          showSuccess(`Consulta de ${meses[index]} completada y procesada`);
        }

        // Actualizar estado local (el backend ya marcó cargado y procesado)
        setStatus(prevStatus => {
          const updated = [...prevStatus];
          updated[index] = { cargado: true, procesado: true };
          return updated;
        });
      } else {
        showError(response.data.error || 'Error en la consulta');
      }
    } catch (error) {
      console.error('Error al consultar SII:', error);
      const errorMsg = error.response?.data?.error || 'Error al consultar el SII';
      showError(errorMsg);
    } finally {
      const resetLoading = [...loadingSii];
      resetLoading[index] = false;
      setLoadingSii(resetLoading);
    }
  };

  const mesConsultando = loadingSii.findIndex(v => v);

  return (
    <div className="carga-compras">
      {loadingSii.some(v => v) && (
        <div className="sii-loading-overlay">
          <div className="sii-loading-modal">
            <div className="sii-loading-spinner"></div>
            <h3>Consultando SII</h3>
            {mesConsultando >= 0 && <p>{meses[mesConsultando]}</p>}
            <small>Por favor espere, no cierre esta ventana</small>
          </div>
        </div>
      )}
      <h1>Carga de Facturas de Venta</h1>

      <label htmlFor="yearSelect">Selecciona un Año:</label>
      <select
        id="yearSelect"
        value={year}
        onChange={(e) => setYear(e.target.value)}
      >
        <option value="">Selecciona un año</option>
        {[...Array(3)].map((_, i) => (
          <option key={i} value={2024 + i}>
            {2024 + i}
          </option>
        ))}
      </select>

      <table>
        <thead>
          <tr>
            <th>Mes</th>
            <th>Archivo</th>
            <th>Acción</th>
            <th>Cargado</th>
            <th>Procesado</th>
            <th>Consultar SII</th>
            <th>Eliminar</th>
          </tr>
        </thead>
        <tbody>
          {meses.map((mes, index) => (
            <tr key={mes}>
              <td>{mes}{mesCerrado(index) && ' (Cerrado)'}</td>
              <td>
                <input
                  type="file"
                  accept=".csv"
                  onChange={(e) => handleFileChange(index, e.target.files[0])}
                  disabled={bloqueado(index)}
                />
              </td>
              <td>
                <button
                  onClick={() => handleUpload(index)}
                  disabled={bloqueado(index)}
                  title={motivoBloqueo(index)}
                >
                  Cargar
                </button>
              </td>
              <td className={status[index].cargado ? 'status-true' : ''}>
                {status[index].cargado ? 'Sí' : 'No'}
              </td>
              <td className={status[index].procesado ? 'status-true' : ''}>
                {status[index].procesado ? 'Sí' : 'No'}
              </td>
              <td>
                <button
                  onClick={() => consultarSii(index)}
                  disabled={loadingSii[index] || bloqueado(index)}
                  title={motivoBloqueo(index)}
                >
                  {loadingSii[index] ? 'Consultando...' : 'Consultar SII'}
                </button>
              </td>
              <td>
                <button
                  onClick={() => handleEliminar(index)}
                  disabled={mesCerrado(index) || !status[index].cargado}
                  title={mesCerrado(index) ? 'Mes cerrado' : 'Elimina las ventas del mes y sus asientos contables'}
                >
                  Eliminar
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );



    
};


export default CargaVentas;